import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
// UPPDRAG-8 PR-E: dual-write dispute admin-resolved system messages.
import { sendLeagueMatchMessage } from "@/lib/chatDualWrite";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Check } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import type { Json } from "@/integrations/supabase/types";
import { getTeamPrimaryPlayers } from "@/lib/teamHelpers";

export default function AdminDisputeQueue() {
  const { t } = useTranslation("admin");
  const { user } = useAuth();
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  const [resolveMatch, setResolveMatch] = useState<any | null>(null);
  const [sets, setSets] = useState<{ home: string; away: string }[]>([]);

  const { data: disputes = [], isLoading } = useQuery({
    queryKey: ["admin-disputes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("league_matches")
        .select("*, court:courts(name)")
        .not("disputed_by", "is", null)
        .eq("dispute_resolved", false)
        .order("disputed_at", { ascending: false });
      if (error) throw error;

      const teamIds = [...new Set((data ?? []).flatMap((m) => [m.home_team_id, m.away_team_id]))];
      // TASK-67 PR-D: dropped player1_id, player2_id from SELECT — same
      // pattern as AdminRescheduleQueue. Identity sourced from
      // team_members[role='primary'], canonical pair = first 2 by joined_at.
      // Phase A.1 cleanup: embed customer_id + customer:customers join so
      // customer-only primaries also resolve their display name in the
      // dispute team-label. Notify branch still uses user_id only.
      const { data: teams } = await supabase
        .from("teams")
        .select(`id, team_name, members:team_members(
          user_id, customer_id, role, joined_at,
          profile:profiles!team_members_user_id_fkey(full_name),
          customer:customers!team_members_customer_id_fkey(full_name, email)
        )`)
        .in("id", teamIds);

      type TeamMemberEmbed = {
        user_id: string | null;
        customer_id: string | null;
        role: "primary" | "reserve";
        joined_at: string;
        profile: { full_name: string | null } | null;
        customer: { full_name: string | null; email: string | null } | null;
      };
      type TeamLite = {
        id: string;
        team_name: string | null;
        members: TeamMemberEmbed[] | null;
      };
      const typedTeams = (teams ?? []) as unknown as TeamLite[];

      // First 2 primaries by joined_at. Phase A.1: label uses dual-identity;
      // notify branch (player1_id/player2_id) stays user-only.
      const teamPrimaryPair = new Map<string, [string | null, string | null]>();
      const teamPrimaryMembers = new Map<string, [TeamMemberEmbed | null, TeamMemberEmbed | null]>();
      for (const t of typedTeams) {
        const allPrimaries = getTeamPrimaryPlayers(t.members ?? []);
        teamPrimaryMembers.set(t.id, [allPrimaries[0] ?? null, allPrimaries[1] ?? null]);
        const userOnly = allPrimaries.filter((m) => m.user_id);
        teamPrimaryPair.set(t.id, [
          userOnly[0]?.user_id ?? null,
          userOnly[1]?.user_id ?? null,
        ]);
      }

      const playerIds = [
        ...new Set(
          Array.from(teamPrimaryPair.values())
            .flatMap(([a, b]) => [a, b])
            .filter((id): id is string => id !== null),
        ),
      ];
      const { data: profiles } = playerIds.length > 0
        ? await supabase
            .from("public_profiles" as any)
            .select("id, full_name")
            .in("id", playerIds)
        : { data: [] as { id: string; full_name: string | null }[] };

      const profileMap = Object.fromEntries((profiles ?? []).map((p) => [p.id, p]));
      const teamMap = Object.fromEntries(typedTeams.map((t) => {
        const [p1, p2] = teamPrimaryPair.get(t.id) ?? [null, null];
        const [m1, m2] = teamPrimaryMembers.get(t.id) ?? [null, null];
        const nameOf = (m: TeamMemberEmbed | null, userIdFallback: string | null): string => {
          if (!m) return "?";
          const profileName = userIdFallback ? profileMap[userIdFallback]?.full_name : null;
          if (profileName) return profileName;
          const profileEmbed = m.profile?.full_name?.trim();
          if (profileEmbed) return profileEmbed;
          const customerName = m.customer?.full_name?.trim();
          if (customerName) return customerName;
          const customerEmail = m.customer?.email?.trim();
          return customerEmail || "?";
        };
        const n1 = nameOf(m1, p1);
        const n2 = nameOf(m2, p2);
        return [t.id, {
          ...t,
          // TASK-67 PR-D: synthesize player1_id / player2_id from the
          // first 2 primaries. Downstream resolveMutation reads these
          // as if they were the legacy columns.
          player1_id: p1,
          player2_id: p2,
          label: t.team_name || `${n1} / ${n2}`,
        }];
      }));

      return (data ?? []).map((m) => ({
        ...m,
        home_team: teamMap[m.home_team_id],
        away_team: teamMap[m.away_team_id],
      }));
    },
  });

  const resolveMutation = useMutation({
    mutationFn: async () => {
      if (!resolveMatch || !user) return;

      const resultSets = sets.map((s) => ({
        home: parseInt(s.home) || 0,
        away: parseInt(s.away) || 0,
      }));

      const setsHome = resultSets.filter((s) => s.home > s.away).length;
      const setsAway = resultSets.filter((s) => s.away > s.home).length;

      const finalResult = {
        sets: resultSets,
        points_home: setsHome,
        points_away: setsAway,
        resolved_by_admin: true,
      };

      const { error } = await supabase
        .from("league_matches")
        .update({
          result: finalResult as unknown as Json,
          dispute_resolved: true,
          confirmed_by: user.id,
          confirmed_at: new Date().toISOString(),
        })
        .eq("id", resolveMatch.id);
      if (error) throw error;

      // System message — UPPDRAG-8 PR-E: dual-write + structured metadata.
      await sendLeagueMatchMessage(supabase, {
        matchId: resolveMatch.id,
        senderUserId: user.id,
        body: `🔨 ${t("disputeQueue.adminResolvedMessage")}`,
        isSystem: true,
        data: {
          type: "dispute_admin_resolved",
          admin_user_id: user.id,
        },
      });

      // Notify players — TASK-67 PR-D: home_team/away_team are the
      // synthesized objects from the queryFn above. Their player1_id /
      // player2_id fields are populated from the first 2 primaries by
      // joined_at (team_members), NOT direct DB reads of the dropped
      // teams.player1_id / player2_id columns.
      const playerIds = [
        resolveMatch.home_team?.player1_id,
        resolveMatch.home_team?.player2_id,
        resolveMatch.away_team?.player1_id,
        resolveMatch.away_team?.player2_id,
      ].filter(Boolean);

      if (playerIds.length > 0) {
        await supabase.from("notifications").insert(
          playerIds.map((uid: string) => ({
            user_id: uid,
            organization_id: organization!.id,  // PR-A: org-scope per Phase 1 RLS
            title: t("disputeQueue.disputeResolvedTitle"),
            body: t("disputeQueue.disputeResolvedBody"),
            type: "dispute_resolved",
            data: { match_id: resolveMatch.id },
          }))
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-disputes"] });
      queryClient.invalidateQueries({ queryKey: ["admin-league-matches"] });
      toast.success(t("admin:toast.disputeResolved"));
      setResolveMatch(null);
    },
    onError: (err: Error) => toast.error(err.message || t("toast.couldNotResolveDispute")),
  });

  const openResolve = (match: any) => {
    const result = match.result as any;
    const existingSets = result?.sets ?? [{ home: 0, away: 0 }, { home: 0, away: 0 }, { home: 0, away: 0 }];
    setSets(existingSets.map((s: any) => ({ home: String(s.home ?? 0), away: String(s.away ?? 0) })));
    setResolveMatch(match);
  };

  if (isLoading) return <p className="text-sm text-muted-foreground">{t("disputeQueue.loading")}</p>;
  if (disputes.length === 0) return <p className="text-sm text-muted-foreground">{t("disputeQueue.noActiveDisputes")}</p>;

  return (
    <div className="space-y-3">
      {disputes.map((match: any) => {
        const result = match.result as any;
        return (
          <Card key={match.id}>
            <CardContent className="py-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <Shield className="h-4 w-4 text-destructive" />
                  <span className="font-medium">
                    {match.home_team?.label ?? "?"} vs {match.away_team?.label ?? "?"}
                  </span>
                  <Badge variant="secondary" className="text-xs">R{match.round}</Badge>
                </div>
              </div>

              {result?.sets && (
                <div className="flex gap-3 text-sm">
                  <span className="text-muted-foreground">{t("disputeQueue.reportedResult")}:</span>
                  {result.sets.map((s: any, i: number) => (
                    <span key={i}>{s.home}-{s.away}</span>
                  ))}
                </div>
              )}

              <Button size="sm" onClick={() => openResolve(match)} className="gap-1">
                <Check className="h-3 w-3" /> {t("disputeQueue.resolveDispute")}
              </Button>
            </CardContent>
          </Card>
        );
      })}

      <Dialog open={!!resolveMatch} onOpenChange={(o) => !o && setResolveMatch(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("disputeQueue.resolveDialogTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              {resolveMatch?.home_team?.label} vs {resolveMatch?.away_team?.label}
            </div>
            {sets.map((set, i) => (
              <div key={i} className="flex items-center gap-2">
                <Label className="w-12 text-xs">Set {i + 1}</Label>
                <Input type="number"
                  className="w-16"
                  value={set.home}
                  onChange={(e) => {
                    const newSets = [...sets];
                    newSets[i] = { ...newSets[i], home: e.target.value };
                    setSets(newSets);
                  }}
                />
                <span>-</span>
                <Input type="number"
                  className="w-16"
                  value={set.away}
                  onChange={(e) => {
                    const newSets = [...sets];
                    newSets[i] = { ...newSets[i], away: e.target.value };
                    setSets(newSets);
                  }}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveMatch(null)}>{t("disputeQueue.cancel")}</Button>
            <Button onClick={() => resolveMutation.mutate()} disabled={resolveMutation.isPending}>
              {t("disputeQueue.confirmResult")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
